// Change Password Page
// Purpose: Allows users to change their password when required (e.g., after admin password reset)

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { LockClosedIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import api from '@/services/api';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import { PublicHeader } from '@/components/public/PublicHeader';

export const ChangePasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const isRequired = searchParams.get('required') === 'true';

  useEffect(() => {
    // If password change is required but user is not authenticated, redirect to login
    if (isRequired && !user) {
      navigate('/');
    }
  }, [isRequired, user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error(t('auth.passwordsDoNotMatch') || 'Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error(t('auth.passwordTooShort') || 'Password must be at least 8 characters long');
      return;
    }

    if (!isRequired && !currentPassword) {
      toast.error('Current password is required');
      return;
    }

    setIsLoading(true);

    try {
      // Required password change after admin invite/reset — must hit the real route
      // (/users/profile/change-password does not exist and left require_password_change stuck)
      await api.post('/users/change-password', {
        currentPassword: isRequired ? undefined : currentPassword,
        newPassword
      });
      
      toast.success(t('auth.passwordChangedSuccessfully') || 'Password changed successfully');
      navigate('/dashboard', { replace: true });
    } catch (error: any) {
      console.error('Password change error:', error);
      
      // Extract error message properly
      let errorMessage = 'Failed to change password';
      if (error?.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData?.error) {
          errorMessage = typeof errorData.error === 'string' 
            ? errorData.error 
            : (errorData.error?.message || errorMessage);
        } else if (errorData?.message) {
          errorMessage = typeof errorData.message === 'string' ? errorData.message : errorMessage;
        }
      } else if (error?.message) {
        errorMessage = typeof error.message === 'string' ? error.message : errorMessage;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isRequired && !user) {
    return null; // Will redirect
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <PublicHeader />
      
      <main className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              {isRequired ? 'Set Your New Password' : 'Change Password'}
            </h2>
            {isRequired && (
              <p className="mt-2 text-center text-sm text-gray-600">
                Your password was reset by an administrator. Please set a new password to continue.
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            {!isRequired && (
              <div className="flex items-center space-x-3">
                <LockClosedIcon className="h-5 w-5 text-[#1a5d2e] flex-shrink-0" />
                <div className="flex-1 relative">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Current Password"
                    className="w-full py-3 px-3 pr-10 border-2 border-[#1a5d2e] rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1a5d2e] focus:border-transparent text-center"
                    required={!isRequired}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#1a5d2e] hover:text-[#0d5d2c]"
                  >
                    {showCurrentPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            )}

            <div className="flex items-center space-x-3">
              <LockClosedIcon className="h-5 w-5 text-[#1a5d2e] flex-shrink-0" />
              <div className="flex-1 relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="New Password"
                  className="w-full py-3 px-3 pr-10 border-2 border-[#1a5d2e] rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1a5d2e] focus:border-transparent text-center"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#1a5d2e] hover:text-[#0d5d2c]"
                >
                  {showNewPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <LockClosedIcon className="h-5 w-5 text-[#1a5d2e] flex-shrink-0" />
              <div className="flex-1 relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm New Password"
                  className="w-full py-3 px-3 pr-10 border-2 border-[#1a5d2e] rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1a5d2e] focus:border-transparent text-center"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#1a5d2e] hover:text-[#0d5d2c]"
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-[#1a5d2e] hover:bg-[#0d5d2c] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1a5d2e] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Changing Password...' : (isRequired ? 'Set Password' : 'Change Password')}
              </button>
            </div>
          </form>
        </div>
      </main>

    </div>
  );
};

